import { createSelector } from '@reduxjs/toolkit';

import { AccountType, RootState, ScribeState, TemplateState } from 'js/types';

export const getProjectById = ({ scribes }: { scribes: ScribeState }, scribeId: string | number) => {
  const scribeIdToUse = typeof scribeId === 'number' ? scribeId : parseInt(scribeId, 10);

  return scribes && scribes.allScribes && scribes.allScribes.find(scribe => scribe.id === scribeIdToUse);
};
export const getScribeById = getProjectById;

export const getTemplateById = ({ templates }: { templates: TemplateState }, templateId: string) => {
  return (
    templates?.templatesByCategory
      .flatMap(category => category.templates)
      .find(template => template.id === templateId)
  );
};

export const getActiveElements = ({ scribes }: { scribes: ScribeState }) => scribes.activeElements;

export const getUndoObjects = ({ scribes }: { scribes: ScribeState }, scribeId: number) => ({
  activeScribe: getScribeById({ scribes }, scribeId),
  activeScribePast: scribes.activeScribePast,
  activeScribeFuture: scribes.activeScribeFuture
});

export const getUserAccountType = (state: RootState): AccountType | undefined => {
  return 'accountType' in state.auth.currentUser ? state.auth.currentUser.accountType : undefined;
};

export const getUser = (state: RootState) => state.auth.currentUser;

export const getImageCropData = (state: RootState) => state.cropping.imageCrop;

export const getCursorsFromState = (state: RootState) =>
  Object.values(state.cursors).reduce((allCursors, cursorGroup) => [...allCursors, ...cursorGroup], []);

export const getUserAudioAssets = (state: RootState) => state.audio.userAudioAssets;

export const isAdvancedTimelineOpen = (state: RootState) => state.ui.newTimeline;

export const getImageCategories = (state: RootState) => state.images.libraries;

const selectedFilters = (state: RootState) => state.imageSearch.selectedFilters;
const visualStyleFilter = (state: RootState) => state.imageSearch.visualStyleFilter;
const facets = (state: RootState) => state.imageSearch.facets;

export const getImageSearchFilters = createSelector(
  [facets, selectedFilters, visualStyleFilter],
  (facets, selectedFilters, visualStyleFilter) => {
    const allActiveFilters = { ...visualStyleFilter, ...selectedFilters };
    const filterMap = new Map<
      string,
      {
        title: string;
        values: { name: string; slug: string; facetId: string; selected?: boolean; disabled?: boolean }[];
      }
    >();

    facets.forEach(facet => {
      const values = facet.values.map(value => ({
        name: value.value,
        slug: value.slug,
        facetId: facet.id,
        selected: allActiveFilters[facet.id]?.includes(value.slug),
        disabled: value.disabled,
        disabledByVisualStyle: !!visualStyleFilter[facet.id]?.includes(value.slug)
      }));

      const existing = filterMap.get(facet.name);
      if (existing) {
        existing.values.push(...values);
      } else {
        filterMap.set(facet.name, { title: facet.name, values });
      }
    });

    return {
      filters: Array.from(filterMap.values()),
      selectedFilters
    };
  }
);

export const getImageSearchMode = (state: RootState) => state.imageSearch.activeSearchMode;

export const getCharacterSeedTerms = (state: RootState) => state.imageSearch.characterSeedTerms;

export const getSelectedCharacter = (state: RootState) => ({
  characterName: state.imageSearch.selectedCharacterName,
  characterStyle: state.imageSearch.selectedCharacterStyle
});

const penCursorsSelector = (state: RootState) => state.cursors.penDraw;
const handCursorsSelector = (state: RootState) => state.cursors.handDraw;
export const getCursorsSelector = createSelector(
  [penCursorsSelector, handCursorsSelector],
  (penCursors, handCursors) => {
    return [...penCursors, ...handCursors];
  }
);
